import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiCallError, call } from "@/api.ts";
import type {
  DraftFinding,
  DraftPreset,
  LayoutDraftResponse,
  SkeletonBox,
  SkeletonRoute,
} from "@/api.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { EmptyState } from "@/components/ui/empty-state.tsx";
import { Segmented } from "@/components/ui/segmented.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
  DEBOUNCE_MS,
  describe,
  describeRevision,
  edit,
  hasErrors,
  LIVE_TIER_REASON,
  openEditor,
  publishRefusal,
  refused,
  saved,
  saveRefusal,
  shownFindings,
  TIMEOUT_MS,
} from "@/lib/layout-editor.ts";
import type { EditorState } from "@/lib/layout-editor.ts";
import { refusal, roleRefusal } from "@/lib/refusal.ts";
import { canWrite, useAuthenticated } from "@/session.tsx";
import { useShells } from "@/hooks.ts";

/**
 * Screen 1e: the layout document as text, with continuous validation and a picture.
 *
 * Option C of ADR 0005. The console holds a string and posts it back; the findings, the fixes and
 * the skeleton all arrive pre-rendered from `/layout/draft`. Nothing in this file knows what a
 * region is called or what makes a route valid, and `console.spec.ts` checks that it stays so.
 *
 * One code path for every change: a keystroke, a preset, and a one-click fix all replace the text
 * and let the debounce ask the server. That is why a fix can clear a finding whose field name the
 * console has never seen.
 */
export function LayoutPage() {
  const session = useAuthenticated();
  const shells = useShells(session);
  const writable = canWrite(session.role);
  const cannotWrite = roleRefusal(session.role, "change layouts", writable);

  const [shellId, setShellId] = useState<string | null>(null);
  const [presets, setPresets] = useState<DraftPreset[]>([]);
  /** `undefined` while the draft loads; the editor cannot exist before the server has answered. */
  const [editor, setEditor] = useState<EditorState | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [check, setCheck] = useState<"idle" | "pending" | "failed">("idle");
  const [status, setStatus] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);
  const [tier, setTier] = useState("skeleton");
  const [busy, setBusy] = useState(false);

  const inFlight = useRef<AbortController | null>(null);
  const debounce = useRef<number | null>(null);

  const items = shells.items ?? [];
  useEffect(() => {
    if (shellId || items.length === 0) return;
    setShellId((items.find((shell) => shell.isDefault) ?? items[0])!.shellId);
  }, [items, shellId]);

  const draftPath = shellId ? `/shells/${shellId}/layout/draft` : null;

  /**
   * A keystroke supersedes any check in progress: nothing it would say is about this text. The
   * indicator belongs to whichever request owns `inFlight`, so aborting the owner clears it too —
   * otherwise a save that cancelled a pending check leaves "Checking…" up with nothing in flight.
   */
  function cancelPending() {
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    debounce.current = null;
    if (inFlight.current) {
      inFlight.current.abort();
      inFlight.current = null;
      setCheck("idle");
    }
  }

  useEffect(() => {
    if (!draftPath) return;
    let live = true;
    cancelPending();
    setEditor(undefined);
    setLoadError(null);
    setStatus(null);
    setCheck("idle");
    call<LayoutDraftResponse>(session, draftPath)
      .then((body) => {
        if (!live) return;
        setPresets(body.presets);
        setEditor(openEditor(body.draft));
      })
      .catch((cause: Error) => {
        if (live) setLoadError(cause.message);
      });
    return () => {
      live = false;
      cancelPending();
    };
    // `cancelPending` closes over refs only, so it is deliberately not a dependency.
  }, [session, draftPath]);

  async function describeText(text: string) {
    if (!draftPath) return;
    const controller = new AbortController();
    inFlight.current = controller;
    // A check that does not answer is not allowed to hang the indicator forever — and the local
    // text is untouched either way. No retry: the next keystroke is the retry.
    const timeout = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
    setCheck("pending");
    try {
      const body = await call<LayoutDraftResponse>(session, draftPath, {
        method: "POST",
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });
      if (body.draft)
        setEditor((state) => (state ? describe(state, body.draft!) : state));
      if (inFlight.current === controller) setCheck("idle");
    } catch {
      // Superseded by a newer edit: not a failure, and nothing to say.
      if (inFlight.current !== controller) return;
      setCheck("failed");
    } finally {
      window.clearTimeout(timeout);
      if (inFlight.current === controller) inFlight.current = null;
    }
  }

  function change(text: string) {
    cancelPending();
    setEditor((state) => (state ? edit(state, text) : state));
    debounce.current = window.setTimeout(() => {
      void describeText(text);
    }, DEBOUNCE_MS);
  }

  async function save() {
    if (!editor || !draftPath) return;
    const text = editor.text;
    cancelPending();
    setBusy(true);
    setStatus(null);
    try {
      const body = await call<LayoutDraftResponse>(session, draftPath, {
        method: "POST",
        body: JSON.stringify({ text, save: true }),
      });
      if (body.draft)
        setEditor((state) => (state ? saved(state, body.draft!) : state));
      setStatus({
        tone: "ok",
        text: "Saved. Nothing a visitor sees has changed yet — publish, then point a channel at it.",
      });
    } catch (cause) {
      // Two 422 shapes leave this route: findings, and the schema's `{ path, message }` issues for
      // a body it would not read at all. Only the first is something the findings list can draw.
      if (
        cause instanceof ApiCallError &&
        cause.status === 422 &&
        isFindings(cause.details)
      ) {
        setEditor((state) =>
          state ? refused(state, cause.details as DraftFinding[], text) : state,
        );
      } else {
        setStatus({
          tone: "error",
          text: cause instanceof Error ? cause.message : String(cause),
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!shellId) return;
    setBusy(true);
    setStatus(null);
    try {
      await call(session, `/shells/${shellId}/publish`, {
        method: "POST",
        body: "{}",
      });
      setStatus({
        tone: "ok",
        text: "Published as a new shell snapshot. It is served once a channel points at it — see Channels; the Audit page records this publish.",
      });
    } catch (cause) {
      setStatus({
        tone: "error",
        text: cause instanceof Error ? cause.message : String(cause),
      });
    } finally {
      setBusy(false);
    }
  }

  const shown = editor ? shownFindings(editor) : [];
  const cannotSave = editor
    ? refusal(cannotWrite, saveRefusal(editor))
    : undefined;
  // Findings are consulted only when they describe this text; between a keystroke and its answer
  // they describe something else, and refusing Publish on that would be a lie for half a second.
  const cannotPublish = editor
    ? refusal(
        cannotWrite,
        publishRefusal(editor),
        editor.checked && hasErrors(editor.findings)
          ? "The saved draft has errors. Fix them and save first."
          : null,
      )
    : undefined;

  return (
    <div className="grid gap-8">
      <header className="grid gap-2">
        <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">
          Layout
        </h1>
        <p className="max-w-[62ch] text-sm leading-relaxed">
          The document that decides which fragments compose on which routes, and
          where. Saving a draft changes nothing a visitor sees; publishing makes
          a version a channel can point at.
        </p>
      </header>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--color-border)] pb-2.5">
        <label className="grid gap-1 text-[11px] tracking-[0.14em] uppercase">
          Shell
          <select
            className="border-input bg-background rounded-[var(--radius)] border px-2 py-1.5 font-sans text-sm tracking-normal normal-case"
            value={shellId ?? ""}
            disabled={items.length === 0}
            onChange={(event) => setShellId(event.target.value)}
          >
            {items.map((shell) => (
              <option key={shell.shellId} value={shell.shellId}>
                {shell.config.name || "Untitled shell"} · {shell.slug}
                {shell.isDefault ? " (default)" : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap items-center gap-3">
          {editor ? (
            <span className="text-muted-foreground text-[12px]">
              {describeRevision(editor.revision)}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            disabled={busy || !editor || Boolean(cannotSave)}
            title={cannotSave}
            onClick={() => void save()}
          >
            Save draft
          </Button>
          <Button
            size="sm"
            disabled={busy || !editor || Boolean(cannotPublish)}
            title={cannotPublish}
            onClick={() => void publish()}
          >
            Publish
          </Button>
        </div>
      </div>

      {status ? (
        <div
          className={
            status.tone === "ok"
              ? "border-l-[3px] border-[color:var(--color-primary)] bg-[color:var(--color-accent)] px-3.5 py-3"
              : "border-l-[3px] border-[color:var(--color-destructive)] bg-[color:rgba(214,0,108,0.08)] px-3.5 py-3"
          }
        >
          <p className="text-[13.5px] leading-relaxed">{status.text}</p>
        </div>
      ) : null}

      {shells.error || loadError ? (
        <EmptyState
          title="Could not load the layout"
          description={
            shells.error ?? loadError ?? "The server did not say why."
          }
        />
      ) : shells.items && items.length === 0 ? (
        <EmptyState
          title="No shells"
          description="A layout belongs to a shell, and this organization has none."
        />
      ) : !editor ? (
        <div className="grid gap-8 lg:grid-cols-2">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : editor.text === "" ? (
        <PresetPicker
          presets={presets}
          disabled={!writable}
          reason={cannotWrite}
          onPick={change}
        />
      ) : (
        <div className="grid min-w-0 gap-8 lg:grid-cols-2">
          <div className="grid min-w-0 content-start gap-4">
            <Editor
              text={editor.text}
              readOnly={!writable || busy}
              onChange={change}
            />
            <CheckLine check={check} stale={!editor.checked} />
            <Findings
              findings={shown}
              onFix={change}
              disabled={!writable}
              reason={cannotWrite}
            />
          </div>

          <div className="grid min-w-0 content-start gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[11px] tracking-[0.14em] uppercase">
                Preview
              </h2>
              <Segmented
                name="preview-tier"
                value={tier}
                onChange={setTier}
                options={[
                  { value: "skeleton", label: "Skeleton" },
                  {
                    value: "live",
                    label: "Live dataplane",
                    disabled: true,
                    reason: LIVE_TIER_REASON,
                  },
                ]}
              />
            </div>
            {editor.skeleton ? (
              <SkeletonView
                routes={editor.skeleton.routes}
                stale={!editor.checked}
              />
            ) : (
              <EmptyState
                title="Nothing to draw yet"
                description="The text is not a document the server can describe. The findings say why."
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isFindings(details: unknown): details is DraftFinding[] {
  return (
    Array.isArray(details) &&
    details.every(
      (entry) => entry && Array.isArray((entry as DraftFinding).fixes),
    )
  );
}

/**
 * Start from a preset — the only way to get a first document that is not a blank page.
 *
 * The preset text is placed in the editor and nothing else: it is not saved, and the server is
 * asked about it exactly as it would be asked about a paste.
 */
function PresetPicker({
  presets,
  disabled,
  reason,
  onPick,
}: {
  presets: DraftPreset[];
  disabled: boolean;
  reason: string | null;
  onPick(text: string): void;
}) {
  return (
    <section className="grid gap-4">
      <div className="grid gap-1">
        <h2 className="text-[19px] font-semibold">No layout yet</h2>
        <p className="text-muted-foreground max-w-[62ch] text-sm leading-relaxed">
          This shell renders from its mounts. Start a document from a preset,
          then edit it as text; nothing is saved until you say so.
        </p>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {presets.map((preset) => (
          <li key={preset.id}>
            <button
              type="button"
              disabled={disabled}
              title={reason ?? undefined}
              onClick={() => onPick(preset.text)}
              className="hover:bg-muted grid w-full gap-1 border border-[color:var(--color-border)] px-4 py-3.5 text-left disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="font-semibold">{preset.name}</span>
              <span className="text-muted-foreground text-[13px]">
                {preset.description}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The text. A plain textarea, sized to its content.
 *
 * The handoff's rule is that the pre fits its track — a clipped closing brace on the screen whose
 * point is a schema-validated document is the worst defect available here. So the box grows with
 * the text up to a ceiling far above any real document, and past it scrolls rather than clips.
 */
const MAX_EDITOR_HEIGHT = 1600;

function Editor({
  text,
  readOnly,
  onChange,
}: {
  text: string;
  readOnly: boolean;
  onChange(text: string): void;
}) {
  const area = useRef<HTMLTextAreaElement>(null);

  // Before paint, not after: measured in `useEffect` a newline paints one frame clipped, with a
  // scrollbar flash. And the parent is pinned during the shrink-to-measure, because collapsing a
  // 1600px box to zero mid-document lets the browser clamp the window's scroll position — the
  // classic auto-grow jump, worst exactly where it matters: a long document on a short screen.
  useLayoutEffect(() => {
    const element = area.current;
    const parent = element?.parentElement;
    if (!element || !parent) return;
    parent.style.minHeight = `${parent.offsetHeight}px`;
    element.style.height = "0px";
    element.style.height = `${Math.min(element.scrollHeight, MAX_EDITOR_HEIGHT)}px`;
    parent.style.minHeight = "";
  }, [text]);

  return (
    <div className="min-w-0">
      <textarea
        ref={area}
        data-testid="layout-text"
        aria-label="Layout document"
        value={text}
        readOnly={readOnly}
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
        className="bg-muted focus-visible:outline-ring block w-full resize-none overflow-x-auto overflow-y-auto px-[18px] py-4 font-mono text-[11.5px] leading-[1.46] whitespace-pre focus-visible:outline-2"
        style={{ tabSize: 2 }}
      />
    </div>
  );
}

function CheckLine({
  check,
  stale,
}: {
  check: "idle" | "pending" | "failed";
  stale: boolean;
}) {
  const text =
    check === "pending"
      ? "Checking…"
      : check === "failed"
        ? "Could not reach the server. Your text is unchanged; the findings below may be out of date."
        : stale
          ? "Findings refer to an earlier version of the text."
          : "Findings are current.";
  return (
    <p
      role="status"
      className={`text-[12px] ${check === "failed" ? "text-destructive" : "text-muted-foreground"}`}
    >
      {text}
    </p>
  );
}

/** Memoized: a keystroke re-renders the page, and neither the findings nor the picture changed. */
const Findings = memo(function Findings({
  findings,
  onFix,
  disabled,
  reason,
}: {
  findings: DraftFinding[];
  onFix(text: string): void;
  disabled: boolean;
  reason: string | null;
}) {
  const errors = findings.filter(
    (finding) => finding.severity === "error",
  ).length;
  if (findings.length === 0) return null;
  return (
    <section className="grid gap-3" aria-label="Findings">
      <h2 className="text-[11px] tracking-[0.14em] uppercase">
        {findings.length} {findings.length === 1 ? "finding" : "findings"}
        {errors > 0
          ? ` · ${errors} ${errors === 1 ? "blocks" : "block"} saving`
          : ""}
      </h2>
      <ul className="divide-border grid divide-y border-y border-[color:var(--color-border)]">
        {findings.map((finding, index) => (
          <li
            key={`${finding.code}-${finding.path}-${index}`}
            className="grid gap-1.5 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  finding.severity === "error" ? "destructive" : "warning"
                }
              >
                {finding.severity === "error" ? "finding" : "note"}
              </Badge>
              <span className="font-mono text-[12px] break-all">
                {finding.code}
              </span>
              {finding.path ? (
                <span className="text-muted-foreground font-mono text-[12px] break-all">
                  {finding.path}
                </span>
              ) : null}
            </div>
            <p className="text-sm leading-relaxed">{finding.message}</p>
            {finding.hint ? (
              <p className="text-muted-foreground text-[13px] leading-relaxed">
                {finding.hint}
              </p>
            ) : null}
            {finding.fixes.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {finding.fixes.map((fix) => (
                  <Button
                    key={fix.label}
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    title={
                      reason ??
                      "Replaces the text; the document is reformatted."
                    }
                    onClick={() => onFix(fix.text)}
                  >
                    {fix.label}
                  </Button>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
});

/**
 * The skeleton: every route, every region, drawn from what the server said and nothing else.
 *
 * An unused region is drawn as an absence rather than left out, because "the sidebar is empty on
 * this route" is what an operator opens this screen to check. No chips: the server sends none,
 * and the design's `idle` and `container query` chips describe a product that does not exist.
 */
const SkeletonView = memo(function SkeletonView({
  routes,
  stale,
}: {
  routes: SkeletonRoute[];
  stale: boolean;
}) {
  if (routes.length === 0) {
    return (
      <EmptyState
        title="No routes"
        description="The document describes no routes, so there is nothing to compose."
      />
    );
  }
  return (
    <div
      className={`grid gap-6 ${stale ? "opacity-60" : ""}`}
      data-testid="skeleton"
      aria-busy={stale}
    >
      {routes.map((route, index) => {
        // Regions in the order the server sends them, which is the console's whole knowledge of
        // them: the first spans the page and the rest share a row. Names are printed, not read.
        const [first, ...rest] = route.regions;
        return (
          <section
            key={`${route.match}-${index}`}
            className="bg-muted grid gap-2 p-4"
          >
            <h3 className="font-mono text-[12px] font-semibold break-all">
              {route.match}
            </h3>
            {first ? <RegionBox name={first.region} box={first.box} /> : null}
            {rest.length > 0 ? (
              <div className="grid gap-2 sm:grid-cols-[1fr_minmax(120px,0.4fr)]">
                {[...rest].reverse().map((region) => (
                  <RegionBox
                    key={region.region}
                    name={region.region}
                    box={region.box}
                  />
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
});

function RegionBox({ name, box }: { name: string; box: SkeletonBox | null }) {
  return (
    <div className="grid min-h-16 content-start gap-1.5 border border-dashed border-[color:rgba(32,30,29,0.35)] p-2">
      <span className="text-muted-foreground text-[10px] tracking-[0.14em] uppercase">
        {name}
      </span>
      {box ? (
        <Box box={box} />
      ) : (
        <span className="text-muted-foreground text-[12px] italic">
          not used on this route
        </span>
      )}
    </div>
  );
}

const TONE: Record<SkeletonBox["tone"], string> = {
  screen:
    "border-[color:var(--color-primary)] text-[color:var(--color-primary)]",
  widget:
    "border-[color:var(--color-destructive)] text-[color:var(--color-destructive)]",
  chrome: "border-[color:rgba(32,30,29,0.5)] text-foreground",
  group: "border-[color:rgba(32,30,29,0.25)] text-muted-foreground",
};

function Box({ box }: { box: SkeletonBox }) {
  return (
    <div
      className={`grid gap-1.5 border border-dashed px-2 py-1.5 ${TONE[box.tone]}`}
    >
      <span className="font-mono text-[12px] break-all">{box.label}</span>
      {box.children && box.children.length > 0 ? (
        <div
          className={
            box.arrangement === "row"
              ? "grid gap-1.5 sm:grid-cols-2"
              : "grid gap-1.5"
          }
        >
          {box.children.map((child, index) => (
            <Box key={`${child.label}-${index}`} box={child} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
